import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Sparkles } from 'lucide-react';
import api from '../../services/api';
import ProductCard from '../ui/ProductCard';
import { TooltipProvider } from '../ui/tooltip';

const CategoryProductRow = ({ category }) => {
  const { data: products, isLoading } = useQuery({
    queryKey: ['products-by-category', category._id || category.id],
    queryFn: async () => {
      const params = {
        category: category._id || category.id,
        limit: 6,
        sortBy: 'newest'
      };

      const response = await api.get('/products', { params });

      if (response.data.success) {
        return response.data.data.products;
      }
      return [];
    },
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <div className="mb-8 md:mb-12">
        <div className="flex items-center justify-between mb-4 md:mb-6">
          <div className="h-8 w-48 bg-gray-200 animate-pulse rounded"></div>
        </div>
        <div className="flex overflow-x-auto lg:grid lg:grid-cols-6 gap-3 md:gap-6 snap-x snap-mandatory pb-4 -mx-2 px-2 lg:mx-0 lg:px-0 lg:overflow-visible scrollbar-hide">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="w-[45%] sm:w-[30%] lg:w-auto flex-shrink-0 snap-start h-64 bg-gray-200 animate-pulse rounded-mobile-lg shadow-mobile"></div>
          ))}
        </div>
      </div>
    );
  }

  if (!products || products.length === 0) return null;

  return (
    <div className="mb-6 md:mb-8 border-b border-gray-100 pb-6 md:pb-8 last:border-0 last:pb-0">
      <div className="flex items-center justify-between mb-3 md:mb-4">
        <h2 className="text-xl md:text-2xl font-bold text-[#111827] flex items-center gap-2">
          {category.name}
        </h2>
        <Link
          to={`/category/${category.slug}`}
          className="text-sm md:text-base font-bold text-[#1E3A8A] hover:text-blue-800 flex items-center transition-colors group"
        >
          View All <span className="ml-1 transition-transform group-hover:translate-x-1">›</span>
        </Link>
      </div>

      <TooltipProvider>
        <div className="flex overflow-x-auto lg:grid lg:grid-cols-6 gap-3 md:gap-6 snap-x snap-mandatory pb-4 -mx-2 px-2 lg:mx-0 lg:px-0 lg:overflow-visible scrollbar-hide">
          {products.slice(0, 6).map((product) => (
            <div key={product._id || product.id} className="w-[45%] sm:w-[30%] lg:w-auto flex-shrink-0 snap-start">
              <ProductCard product={product} />
            </div>
          ))}
        </div>
      </TooltipProvider>
    </div>
  );
};

const FeaturedCategoryProducts = ({ categories, isLoading, title = "Featured Categories" }) => {
  if (isLoading) {
    return (
      <section className="bg-white py-6 md:py-8">
        <div className="w-full md:container mx-auto px-2 sm:px-4">
          <div className="mb-6 md:mb-8">
            <div className="h-8 w-64 bg-gray-200 animate-pulse rounded mb-2"></div>
            <div className="h-4 w-96 bg-gray-200 animate-pulse rounded"></div>
          </div>
          {[...Array(2)].map((_, i) => (
            <div key={i} className="mb-8 md:mb-12">
              <div className="h-8 w-48 bg-gray-200 animate-pulse rounded mb-4 md:mb-6"></div>
              <div className="flex overflow-x-auto lg:grid lg:grid-cols-6 gap-3 md:gap-6 snap-x snap-mandatory pb-4 -mx-2 px-2 lg:mx-0 lg:px-0 lg:overflow-visible scrollbar-hide">
                {[...Array(6)].map((_, j) => (
                  <div key={j} className="w-[45%] sm:w-[30%] lg:w-auto flex-shrink-0 snap-start h-64 bg-gray-200 animate-pulse rounded-mobile-lg shadow-mobile"></div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (!categories || categories.length === 0) return null;

  return (
    <section className="bg-white py-4 md:py-6">
      <div className="w-full md:container mx-auto px-2 sm:px-4">
        {/* Main Section Header */}
        <div className="mb-4 md:mb-6">
          <h2 className="text-xl md:text-2xl font-bold text-[#111827] flex items-center gap-2 mb-2">
            {title} <Sparkles className="w-5 h-5 md:w-6 md:h-6 text-red-500" strokeWidth={2} />
          </h2>
          <p className="text-sm md:text-base font-medium text-gray-500">
            Explore products from our top featured categories
          </p>
        </div>

        {/* Category Rows */}
        {categories.map((category) => (
          <CategoryProductRow key={category._id || category.id} category={category} />
        ))}
      </div>
    </section>
  );
};

export default FeaturedCategoryProducts;
